import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import type { StatCardConfig } from '../types/totalAnalytics.types';
import { AnalyticsStatIcon } from './AnalyticsStatIcon';
import { separateNumber } from '@/utils/function';

type AnalyticsStatCardProps = Omit<StatCardConfig, 'iconKey'> & {
  iconKey: StatCardConfig['iconKey'];
};

export function AnalyticsStatCard({
  title,
  value,
  iconKey,
  cardBgClass,
  iconBgClass,
  infoText,
}: AnalyticsStatCardProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const updateCoords = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const tooltipWidth = Math.min(260, window.innerWidth - 32);

    let left = rect.right - tooltipWidth;
    if (left < 16) left = 16;
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }

    setCoords({
      top: rect.bottom + 6,
      left,
    });
  };

  useEffect(() => {
    if (!showTooltip) return;
    updateCoords();

    const handleScrollOrResize = () => updateCoords();
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        buttonRef.current &&
        buttonRef.current.contains(e.target as Node)
      ) {
        return;
      }
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node)
      ) {
        setShowTooltip(false);
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div
      className={`relative h-full min-h-[112px] rounded-2xl p-4 shadow-sm ${cardBgClass}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBgClass}`}
            >
              <AnalyticsStatIcon iconKey={iconKey} />
            </div>
            <div className="min-w-0">
              <p className="m-0 text-xs font-semibold text-[var(--dash-muted)]">
                {title}
              </p>
              <p className="m-0 mt-1 text-[22px] leading-tight text-[var(--dash-ink)]">
                {separateNumber(value)}
              </p>
            </div>
          </div>
        </div>

        <button
          ref={buttonRef}
          type="button"
          aria-label={`${title} info`}
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip((prev) => !prev);
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/80 text-[var(--dash-muted)] shadow-sm transition hover:bg-white hover:text-[var(--dash-brand)] focus:outline-none"
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      {showTooltip && coords
        ? createPortal(
            <div
              ref={tooltipRef}
              role="tooltip"
              style={{
                position: 'fixed',
                top: `${coords.top}px`,
                left: `${coords.left}px`,
              }}
              className="z-[9999] w-64 max-w-[calc(100vw-32px)] rounded-xl border border-white/10 bg-[#0F1817] p-3 text-xs leading-relaxed text-white shadow-2xl"
            >
              {infoText}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
